import { useEffect, useState } from "react";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { Webhook, Copy, ShieldCheck, ExternalLink, Loader2 } from "lucide-react";

export default function Webhooks() {
  const [urls, setUrls] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lgpd/webhook-urls");
        setUrls(data);
      } catch {
        toast.error("Falha ao obter URLs de webhook");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copiada`);
  };

  const rows = [
    {
      key: "store_redact",
      title: "URL webhook store redact",
      desc: "Enviado quando um lojista desinstala o app. O Magic SIP apaga todos os dados vinculados àquela loja.",
      testid: "webhook-store-redact",
    },
    {
      key: "customers_redact",
      title: "URL webhook customers redact",
      desc: "Enviado quando um consumidor solicita exclusão dos dados dele (LGPD). O Magic SIP não armazena PII de consumidores, mas registra o evento para auditoria.",
      testid: "webhook-customers-redact",
    },
    {
      key: "customers_data_request",
      title: "URL webhook customers data request",
      desc: "Enviado quando um consumidor solicita relatório dos dados armazenados. O Magic SIP registra o evento e informa o lojista de que não retém PII.",
      testid: "webhook-customers-data-request",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 p-6 sm:p-10">
        <header className="mb-8">
          <div className="eyebrow mb-2">Compliance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] flex items-center gap-3">
            <Webhook className="w-8 h-8 text-[#002FA7]" />
            Webhooks LGPD · Nuvemshop
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Os 3 endpoints exigidos pela homologação do Nuvemshop para conformidade com a LGPD.
          </p>
        </header>

        <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4 mb-6 flex items-start gap-3 max-w-3xl">
          <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-900">
            <div className="font-semibold mb-1">Todos os endpoints estão implementados e ativos</div>
            <p className="text-emerald-900/80 text-xs">
              Validação HMAC via <code className="bg-white px-1 rounded">x-linkedstore-hmac-sha256</code>{" "}
              usando o <code className="bg-white px-1 rounded">client_secret</code> do app.
              Eventos registrados em auditoria interna.
            </p>
          </div>
        </div>

        {loading && (
          <div className="text-sm text-zinc-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando URLs…
          </div>
        )}

        {!loading && urls && (
          <div className="space-y-4 max-w-3xl" data-testid="webhook-urls-list">
            {rows.map((r) => (
              <div
                key={r.key}
                className="border border-zinc-200 bg-white rounded-md p-5"
                data-testid={`${r.testid}-card`}
              >
                <div className="font-display font-semibold text-[#09090B] mb-1">{r.title}</div>
                <p className="text-xs text-zinc-500 mb-3">{r.desc}</p>
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={urls[r.key]}
                    className="flex-1 font-mono text-xs bg-zinc-50 border border-zinc-300 rounded-md px-3 py-2 text-zinc-900"
                    data-testid={`${r.testid}-input`}
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <Button
                    onClick={() => copy(urls[r.key], r.title)}
                    variant="outline"
                    className="rounded-md border-zinc-300"
                    data-testid={`${r.testid}-copy`}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 max-w-3xl">
          <a
            href="https://tiendanube.github.io/api-documentation/resources/webhook"
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#002FA7] hover:underline inline-flex items-center gap-1"
            data-testid="webhook-docs-link"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Documentação oficial da Nuvemshop
          </a>
        </div>
      </main>
    </div>
  );
}
